import React, { useState } from 'react';
import styles from './medium-media-card.module.css';
import { MediaItem } from '../../types';
import { getEmbedSrc } from '../../utils/media-utils';

interface MediumMediaCardProps {
  item: MediaItem;
  onPlay?: (item: MediaItem) => void;
}

export default function MediumMediaCard({ item, onPlay }: MediumMediaCardProps) {
  const [showEmbed, setShowEmbed] = useState(false);
  const typeName = item.type === 'podcast_video' ? 'Podcast & Video' : item.type;
  const outletName = typeof item.outlet === 'string' ? item.outlet : item.outlet?.name;
  const outletOrType = outletName || typeName;

  const trackEvent = (eventName: string, additionalParams: Record<string, unknown> = {}) => {
    window.analytics?.trackEvent(eventName, {
      media_id: item.a_id || item.id,
      media_title: item.title,
      media_type: item.type,
      media_outlet: outletName,
      media_url: item.linkUrl,
      media_location: 'home_media_section',
      ...additionalParams
    });
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackEvent('media_watch', {
      method: onPlay ? 'embed_play_overlay' : 'embed_play'
    });
    if (onPlay) {
      onPlay(item);
    } else {
      setShowEmbed(true);
    }
  };

  // Thumbnail: when no embedUrl, allow default (navigate); when embedUrl, play in-place
  const thumbnailClick = item.embedUrl ? handlePlayClick : (e: React.MouseEvent) => trackEvent('media_click', { url: item.linkUrl });

  // When we have an embed, keep user on page: thumbnail and title don't navigate
  const thumbnailHref = item.embedUrl ? '#' : item.linkUrl;
  const thumbnailTarget = item.embedUrl ? undefined : '_blank';
  const thumbnailRel = item.embedUrl ? undefined : 'noopener noreferrer';

  // Title: when embedUrl clicking plays video; when no embedUrl navigates and tracks
  const titleHref = item.embedUrl ? '#' : item.linkUrl;
  const titleClick = item.embedUrl
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        handlePlayClick(e);
      }
    : () => trackEvent('media_click', { url: item.linkUrl });
  const titleTarget = item.embedUrl ? undefined : '_blank';
  const titleRel = item.embedUrl ? undefined : 'noopener noreferrer';

  return (
    <div className={styles.card}>
      <div className={styles.mediaContainer}>
        {showEmbed && item.embedUrl ? (
          <iframe
            src={getEmbedSrc(item.embedUrl)}
            title={item.title}
            className={styles.iframe}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <a
            href={thumbnailHref}
            {...(thumbnailTarget ? { target: thumbnailTarget, rel: thumbnailRel } : {})}
            className={styles.imageLink}
            onClick={thumbnailClick}
          >
            <img
              src={item.imageUrl}
              alt={item.title}
              className={styles.image}
            />
            {item.embedUrl && (
              <div className={styles.playOverlay}>
                <div className={styles.playButton}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className={styles.playIcon}
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
          </a>
        )}
      </div>

      <div className={styles.content}>
        <span className={styles.outlet}>{outletOrType}</span>
        <h3 className={styles.title}>
          <a
            href={titleHref}
            {...(titleTarget ? { target: titleTarget, rel: titleRel } : {})}
            className={styles.titleLink}
            onClick={titleClick}
          >
            {item.title}
          </a>
        </h3>
      </div>
    </div>
  );
}
